import { toast } from "@/shared/hooks/useToast";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { fetchWorkspaceRows } from "../api/workspaceSearch";
import type { WorkspaceSearchParams, WorkspaceSearchRow } from "../types/workspaceSearch";
import { WORKSPACE_SEARCH_CHANNELS } from "../types/workspaceSearch";

export function useWorkspaceSearch() {
    const [orgId, setOrgId] = useState("");
    const [channel, setChannel] = useState(WORKSPACE_SEARCH_CHANNELS[0]);
    const [dwAccountId, setDwAccountId] = useState("");

    const [rows, setRows] = useState<WorkspaceSearchRow[]>([]);

    // DataTable는 "manualPagination: true"라서 page/pageSize는 외부에서 관리
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const mutation = useMutation({
        mutationFn: (params: WorkspaceSearchParams) => fetchWorkspaceRows(params),
        onSuccess: (data) => {
            setRows(data);
            setPage(1);
        },
        onError: (e: unknown) => {
            const msg = e instanceof Error ? e.message : "조회에 실패했습니다.";
            toast({ title: "조회 실패", description: msg, variant: "destructive" });
            setRows([]);
        },
    });

    const canSubmit = useMemo(() => {
        return orgId.trim() !== "" && dwAccountId.trim() !== "" && !!channel;
    }, [orgId, dwAccountId, channel]);

    const submit = async () => {
        if (!canSubmit) return;
        await mutation.mutateAsync({
            orgId: orgId.trim(),
            channel,
            dwAccountId: dwAccountId.trim(),
        });
    };

    // 클라이언트 페이지네이션(원본은 서버 페이징이 없었음)
    const pagedRows = useMemo(() => {
        const start = (page - 1) * pageSize;
        return rows.slice(start, start + pageSize);
    }, [rows, page, pageSize]);

    return {
        form: { orgId, setOrgId, channel, setChannel, dwAccountId, setDwAccountId },
        rows,
        pagedRows,
        totalCount: rows.length,
        page,
        pageSize,
        setPage,
        setPageSize,
        canSubmit,
        submit,
        isLoading: mutation.isPending,
        isError: mutation.isError,
    };
}